import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, TrendingUp, User, LogOut, CreditCard, AlertTriangle, Lock, Menu } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { getLogoOrInitials } from '../utils/companyLogo';
import { getTierColor } from '../utils/tierColors';
import NotificationBell from './NotificationBell';
import { companyRegistrationApi } from '../services/api';

const HeaderProfile = ({ onOpenNavigation, menuTriggerRef, isNavigationOpen }) => {
  const { member, user, logout } = useAuth();
  const { tierConfig } = useTier();
  const navigate = useNavigate();
  const [companyRegistration, setCompanyRegistration] = useState(null);
  const [showUpgradeBlockedDialog, setShowUpgradeBlockedDialog] = useState(false);

  const isExpired = tierConfig?.is_expired === true;
  const membershipExpiryDate = member?.membership_expiry_date ? new Date(member.membership_expiry_date) : null;
  const isValidExpiryDate = membershipExpiryDate instanceof Date && !Number.isNaN(membershipExpiryDate.getTime());
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysRemaining = isValidExpiryDate
    ? Math.ceil((membershipExpiryDate.getTime() - Date.now()) / msPerDay)
    : null;
  const showExpiryWarning = !isExpired && daysRemaining != null && daysRemaining >= 0 && daysRemaining <= 15;
  const expiryWarningText = daysRemaining === 0
    ? 'Your membership expires today'
    : `Your membership expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`;
  const isRegisteredUser = (member?.tier || '').toLowerCase() === 'registered user';
  const isInvitedTeamMember =
    member?.has_admin_rights === false ||
    member?.role === 'Company Member' ||
    Boolean(member?.invited_by);
  const isCompanyApproved = companyRegistration?.status?.toLowerCase() === 'approved';
  const shouldBlockUpgrade = isRegisteredUser && !isInvitedTeamMember && !isCompanyApproved;

  useEffect(() => {
    let cancelled = false;

    const fetchCompanyRegistration = async () => {
      try {
        const response = await companyRegistrationApi.getMyRegistration();
        if (!cancelled && response.data?.exists) {
          setCompanyRegistration(response.data);
        }
      } catch (error) {
        if (!cancelled && error.response?.status !== 404) {
          console.error('Failed to fetch company registration:', error);
        }
      }
    };

    if (isRegisteredUser) {
      fetchCompanyRegistration();
    }

    return () => {
      cancelled = true;
    };
  }, [isRegisteredUser]);

  const handleUpgradeClick = (event) => {
    if (!shouldBlockUpgrade) return;
    event.preventDefault();
    setShowUpgradeBlockedDialog(true);
  };

  return (
    <>
      <div
        className="fixed inset-x-0 top-0 z-[45] flex h-16 items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 sm:px-4 xl:left-64 xl:px-6"
        inert={isNavigationOpen || undefined}
        aria-hidden={isNavigationOpen || undefined}
      >
        <div className="flex min-w-0 items-center gap-2 xl:hidden">
          <Button
            ref={menuTriggerRef}
            type="button"
            variant="ghost"
            size="icon"
            className="min-h-[44px] min-w-[44px] shrink-0"
            aria-label="Open navigation menu"
            onClick={onOpenNavigation}
          >
            <Menu size={22} />
          </Button>
          <span className="hidden truncate text-sm font-semibold text-blue-900 sm:inline sm:text-base">One Globe Alliance</span>
        </div>

        <div className="ml-auto flex min-w-0 items-center justify-end gap-2 sm:gap-3">
          {showExpiryWarning && (
            <div className="hidden min-w-0 items-center rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-800 sm:inline-flex">
              <AlertTriangle size={14} className="mr-2 shrink-0" />
              <span className="max-w-[12rem] truncate">{expiryWarningText}</span>
            </div>
          )}

          {/* Notification Bell */}
          <div className="shrink-0">
            <NotificationBell />
          </div>

          {/* Pay now (expired) vs Upgrade (active membership) */}
          <Button
            asChild
            variant="outline"
            size="sm"
            className={`shrink-0 ${isExpired ? 'text-amber-600 border-amber-400 hover:bg-amber-50 bg-amber-50' : 'text-blue-600 border-blue-300 hover:bg-blue-50'}`}
            aria-label={isExpired ? 'Pay membership renewal' : 'Upgrade membership'}
          >
            <Link to="/upgrade" onClick={handleUpgradeClick}>
              {isExpired ? (
                <>
                  <CreditCard size={16} className="mr-1" />
                  <span className="hidden sm:inline">Pay now</span>
                </>
              ) : (
                <>
                  <TrendingUp size={16} className="mr-1" />
                  <span className="hidden sm:inline">Upgrade</span>
                </>
              )}
            </Link>
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex shrink-0 items-center space-x-2" data-testid="user-menu-btn">
                <img
                  src={getLogoOrInitials(member, member?.company_name || user?.company_name)}
                  alt={member?.company_name || 'User'}
                  className="w-8 h-8 rounded-full object-cover"
                  onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(member?.individual_name || user?.full_name || 'U')}`; }}
                />
                <div className="text-left hidden md:block">
                  <p className="text-sm font-medium text-gray-900">{member?.individual_name || user?.full_name || 'User'}</p>
                  <Badge className={`text-xs ${getTierColor(member?.tier || 'Silver').badge}`}>
                    {member?.tier || 'Silver'}
                  </Badge>
                </div>
                <ChevronDown size={16} className="text-gray-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {shouldBlockUpgrade ? (
                <DropdownMenuItem
                  className="text-gray-400 cursor-not-allowed"
                  onSelect={(event) => event.preventDefault()}
                  disabled
                >
                  <Lock size={16} className="mr-2" />
                  My Profile
                </DropdownMenuItem>
              ) : (
                <Link to="/profile">
                  <DropdownMenuItem className="cursor-pointer">
                    <User size={16} className="mr-2" />
                    My Profile
                  </DropdownMenuItem>
                </Link>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer text-red-600"
                onClick={logout}
              >
                <LogOut size={16} className="mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Dialog open={showUpgradeBlockedDialog} onOpenChange={setShowUpgradeBlockedDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Company Registration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-gray-700">
            <p>

              Complete your company registration and wait for approval before upgrading or making any payment.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowUpgradeBlockedDialog(false)}>
                Close
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => {
                  setShowUpgradeBlockedDialog(false);
                  navigate(companyRegistration ? '/my-company-registration' : '/company-registration');
                }}
              >
                {companyRegistration ? 'View Registration' : 'Complete Registration'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HeaderProfile;
